import { prefersReducedMotion } from '@theme/utilities';

/**
 * Amada Atelier — guided finder.
 *
 * Piece → merchandised collection JSON.
 * Shape / metal → strict membership (tags, title, metafields, synonyms).
 * Budget → quartile bands of that piece's prices, computed once, never hidden.
 */

const SCROLL_MARGIN = 24;
const MAX_POOL_PAGES = 12;
const RESULTS_PER_PAGE = 50;
const SKIP_VALUE = 'any';
const FALLBACK_BANDS = [
  { min: 0, max: 800 },
  { min: 800, max: 1250 },
  { min: 1250, max: 2500 },
  { min: 2500, max: Infinity },
];

const DEFAULT_MATCH = {
  round: ['round diamond', 'round brilliant', 'brilliant', 'round'],
  oval: ['oval diamond', 'oval cut', 'oval'],
  radiant: ['radiant'],
  emerald: ['emerald cut', 'emerald'],
  pear: ['pear shape', 'pear'],
  princess: ['princess'],
  cushion: ['elongated cushion', 'cushion cut', 'cushion'],
  elongated_cushion: ['elongated cushion', 'cushion'],
  marquise: ['marquise'],
  asscher: ['asscher'],
  heart: ['heart'],
  platinum: ['platinum'],
  'white-gold': ['white gold', '18ct white', '18k white', '9ct white', '9k white'],
  'yellow-gold': ['yellow gold', '18ct yellow', '18k yellow', '9ct yellow', '9k yellow'],
  'rose-gold': ['rose gold', 'pink gold', '18ct rose', '18k rose', '9ct rose'],
};

class AmadaAtelierFinder extends HTMLElement {
  #controller = new AbortController();
  #poolCache = new Map();
  #poolInflight = new Map();
  #poolControllers = new Map();
  #ready = false;
  #autoStep = null;
  #cardTemplate = null;
  #bespokeTemplate = null;
  #answers = new Map();
  #pool = [];
  #poolFor = null;
  #priceCache = new Map();
  #bandsForPiece = null;
  #stepCursor = 0;
  #previewOption = null;
  #inBandCount = 0;
  #shown = 0;
  #ranked = [];
  #widened = false;
  #poolFailed = false;
  #selectionVersion = 0;

  connectedCallback() {
    if (this.#tryInit()) return;
    document.addEventListener('DOMContentLoaded', () => this.#tryInit(), {
      once: true,
      signal: this.#controller.signal,
    });
  }

  #tryInit() {
    if (this.#ready) return true;
    if (!this.querySelector('[data-atelier-step]')) return false;
    this.#ready = true;
    this.#init();
    return true;
  }

  disconnectedCallback() {
    this.#controller.abort();
    for (const controller of this.#poolControllers.values()) controller.abort();
    this.#poolControllers.clear();
  }

  get #steps() {
    return [...this.querySelectorAll('[data-atelier-step]')];
  }

  get #resultsTarget() {
    return this.querySelector('[data-atelier-results]');
  }

  get #minResults() {
    return Number(this.dataset.minResults) || 3;
  }

  get #shortlist() {
    return Number(this.dataset.shortlist) || 8;
  }

  #init() {
    const { signal } = this.#controller;

    this.#autoStep = this.#steps.find((step) => !step.querySelector('[data-atelier-option]')) ?? null;
    this.#cardTemplate = this.querySelector('template[data-atelier-card]');
    this.#bespokeTemplate = this.querySelector('template[data-atelier-bespoke]');

    for (const step of this.#steps) {
      const group = step.querySelector('[data-atelier-options]');
      if (!group) continue;

      if (step.dataset.optional === 'true') {
        this.#appendSkipOption(group);
      }

      group.setAttribute('role', 'radiogroup');
      const labelledBy = group.dataset.labelledby;
      if (labelledBy && document.getElementById(labelledBy)) {
        group.setAttribute('aria-labelledby', labelledBy);
      }

      const live = () => [...group.querySelectorAll('[data-atelier-option]')].filter((o) => !o.hidden);
      group.addEventListener('click', (event) => this.#onClick(event, step, live()), { signal });
      group.addEventListener('keydown', (event) => this.#onKeydown(event, live()), { signal });

      // Warm the piece the shopper is about to pick, so the first answer is
      // usually served from cache rather than starting the fetch on click.
      if (step.dataset.param === 'piece') {
        const warm = (event) => this.#prefetchPool(event.target.closest('[data-atelier-option]'));
        group.addEventListener('pointerenter', warm, { capture: true, signal });
        group.addEventListener('focusin', warm, { signal });
      }
    }

    this.querySelector('[data-atelier-reset]')?.addEventListener('click', () => this.#reset(), { signal });
    this.querySelector('[data-atelier-back]')?.addEventListener('click', () => this.#goBack(), { signal });
    this.addEventListener(
      'click',
      (event) => {
        const navSkip = event.target.closest('[data-atelier-nav-skip]');
        if (!navSkip || !this.contains(navSkip)) return;
        event.preventDefault();
        this.#skipCurrent();
      },
      { signal }
    );
    this.querySelector('[data-atelier-more-btn]')?.addEventListener('click', () => this.#loadMore(), { signal });
    this.querySelector('[data-atelier-relax-btn]')?.addEventListener('click', () => this.#relax(), { signal });
    this.querySelector('[data-atelier-chips]')?.addEventListener('click', (event) => this.#onChipRemove(event), {
      signal,
    });

    this.querySelectorAll('[data-atelier-shape-use]').forEach((btn) => {
      btn.textContent = this.dataset.useShapeLabel || 'Use this shape';
      btn.addEventListener('click', () => this.#commitPreview(), { signal });
    });

    this.#bindShapeRails(signal);
    window.history.replaceState(
      { ...(window.history.state ?? {}), amadaAtelier: true, depth: 0 },
      '',
      window.location.href
    );
    window.addEventListener('popstate', () => this.#restoreFromUrl(), { signal });

    this.#applyShowFor();
    this.#restoreFromUrl();
    this.#paintFlow();
  }

  #appendSkipOption(group) {
    const label = this.dataset.skipLabel?.trim();
    if (!label || group.querySelector('[data-atelier-skip]')) return;

    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'amada-atelier-option amada-atelier-option--skip';
    skip.dataset.atelierOption = '';
    skip.dataset.atelierSkip = '';
    skip.dataset.value = '';
    skip.dataset.label = label;
    skip.innerHTML = '<span class="amada-atelier-option__label"></span>';
    skip.querySelector('span').textContent = label;
    group.append(skip);
  }

  #bindShapeRails(signal) {
    for (const step of this.#steps) {
      if (step.dataset.layout !== 'shapes') continue;
      const track = step.querySelector('[data-atelier-options]');
      const prev = step.querySelector('[data-atelier-shape-prev]');
      const next = step.querySelector('[data-atelier-shape-next]');
      if (!track) continue;

      const stepSize = () => {
        const items = [...track.querySelectorAll('[data-atelier-option]')].filter((o) => !o.hidden);
        if (items.length < 2) return track.clientWidth * 0.3;
        return items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left;
      };
      const sync = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        prev?.removeAttribute('hidden');
        next?.removeAttribute('hidden');
        prev?.toggleAttribute('disabled', track.scrollLeft <= 2);
        next?.toggleAttribute('disabled', track.scrollLeft >= max || max <= 0);
      };

      prev?.addEventListener('click', () => track.scrollBy({ left: -stepSize(), behavior: 'smooth' }), { signal });
      next?.addEventListener('click', () => track.scrollBy({ left: stepSize(), behavior: 'smooth' }), { signal });
      track.addEventListener('scroll', sync, { passive: true, signal });
      sync();
    }
  }

  #onClick(event, step, options) {
    const option = event.target.closest('[data-atelier-option]');
    if (!option || !this.contains(option)) return;
    event.preventDefault();
    if (step.dataset.layout === 'shapes' && !option.hasAttribute('data-atelier-skip')) {
      this.#previewShape(step, option);
      return;
    }
    this.#select(step, option, options);
  }

  #onKeydown(event, options) {
    const current = event.target.closest('[data-atelier-option]');
    if (!current) return;
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const index = options.indexOf(current);
    let next = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % options.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + options.length) % options.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = options.length - 1;
    options[next]?.focus();
  }

  #answerFrom(option) {
    const skip = option.hasAttribute('data-atelier-skip');
    return {
      value: skip ? SKIP_VALUE : option.dataset.value ?? '',
      label: option.dataset.label ?? '',
      skip,
      query: option.dataset.query ?? '',
      collection: option.dataset.collection ?? '',
      match: option.dataset.match ?? '',
      shapeKey: option.dataset.shapeKey ?? '',
      metalFamily: option.dataset.metalFamily ?? '',
      min: Number(option.dataset.priceMin) || 0,
      max: Number(option.dataset.priceMax) || 0,
    };
  }

  #select(step, option, options, { advance = true } = {}) {
    const param = step.dataset.param;
    if (!param) return false;

    const alreadyChosen = option.getAttribute('aria-checked') === 'true';
    if (alreadyChosen) {
      option.focus();
      return false;
    }

    for (const item of options) {
      item.setAttribute('aria-checked', 'false');
      item.tabIndex = -1;
    }

    option.setAttribute('aria-checked', 'true');
    option.tabIndex = 0;
    this.#answers.set(param, this.#answerFrom(option));
    if (param === 'piece') {
      this.#pool = [];
      this.#poolFor = null;
      this.#bandsForPiece = null;
      this.#poolFailed = false;
    }

    option.focus();
    this.#applyGates();
    this.#applyShowFor();
    this.#syncUrl('push');
    const selectionVersion = ++this.#selectionVersion;

    const after = async () => {
      // The first answer is the only cold one: nothing is cached yet and the
      // pool is several hundred products. Move to the next question straight
      // away and let the counts arrive underneath, rather than holding the
      // shopper on a dead screen until the whole catalogue has landed.
      const coldPiece = param === 'piece' && this.#answers.has('piece');
      if (coldPiece && advance) this.#afterSelect(step);

      if (this.#answers.has('piece')) {
        await this.#ensurePool();
        if (selectionVersion !== this.#selectionVersion) return;
        if (param === 'piece') {
          await this.#buildAutoBands();
        } else {
          this.#refreshCounts();
        }
      }
      if (selectionVersion !== this.#selectionVersion) return;
      if (advance && !coldPiece) this.#afterSelect(step);
    };

    after();
    return true;
  }

  #previewShape(step, option) {
    this.#previewOption = option;
    for (const item of step.querySelectorAll('[data-atelier-option]')) {
      item.toggleAttribute('data-preview', item === option);
    }

    const preview = step.querySelector('[data-atelier-shape-preview]');
    if (!preview) return;
    preview.hidden = false;

    const title = preview.querySelector('[data-atelier-shape-title]');
    const tagline = preview.querySelector('[data-atelier-shape-tagline]');
    const body = preview.querySelector('[data-atelier-shape-body]');
    const media = preview.querySelector('[data-atelier-shape-media]');
    if (title) title.textContent = option.dataset.label || '';
    if (tagline) {
      tagline.textContent = option.dataset.tagline || '';
      tagline.hidden = !option.dataset.tagline;
    }
    if (body) {
      body.textContent = option.dataset.body || '';
      body.hidden = !option.dataset.body;
    }
    if (media) {
      media.replaceChildren();
      const feature = option.querySelector('.amada-atelier-option__feature-image');
      const thumb = option.querySelector('.amada-atelier-option__image');
      const source = feature || thumb;
      if (source) media.append(source.cloneNode(true));
    }
  }

  #commitPreview() {
    const step = this.#previewOption?.closest('[data-atelier-step]');
    if (!step || !this.#previewOption) return;
    const options = [...step.querySelectorAll('[data-atelier-option]')].filter((o) => !o.hidden);
    if (this.#select(step, this.#previewOption, options, { advance: false })) {
      this.#afterSelect(step);
    }
  }

  async #afterSelect(step) {
    const visible = this.#visibleSteps();
    const index = visible.indexOf(step);
    const isLast = index === visible.length - 1 || step.dataset.param === 'budget';

    if (this.#answers.has('piece') && (isLast || this.#hasEnoughToShow(visible))) {
      if (isLast) {
        this.#shown = this.#shortlist;
        this.#renderResults();
        this.#stepCursor = visible.length;
        this.#paintFlow();
        return;
      }
    }

    if (index >= 0 && index < visible.length - 1) {
      this.#stepCursor = index + 1;
      this.#paintFlow();
      return;
    }

    this.#paintFlow();
  }

  #hasEnoughToShow(visible) {
    return visible.every((step) => {
      if (step.dataset.optional === 'true') return true;
      return this.#answers.has(step.dataset.param);
    });
  }

  #visibleSteps() {
    return this.#steps.filter((step) => {
      if (!this.#stepApplies(step)) return false;
      if (step === this.#autoStep && !this.#answers.has('piece')) return false;
      return true;
    });
  }

  #stepApplies(step) {
    const param = step.dataset.showForParam;
    const values = (step.dataset.showForValues ?? '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    if (!param || !values.length) return true;
    const current = this.#answers.get(param)?.value ?? '';
    return values.includes(current);
  }

  #applyShowFor() {
    for (const step of this.#steps) {
      if (!this.#stepApplies(step) && step.dataset.param) {
        this.#answers.delete(step.dataset.param);
        step.querySelectorAll('[data-atelier-option]').forEach((option) => {
          option.setAttribute('aria-checked', 'false');
          option.removeAttribute('data-preview');
        });
        if (step.dataset.layout === 'shapes') {
          this.#previewOption = null;
          step.querySelector('[data-atelier-shape-preview]')?.setAttribute('hidden', '');
        }
      }
    }
  }

  #applyGates() {
    for (const option of this.querySelectorAll('[data-gate-param]')) {
      const param = option.dataset.gateParam;
      const allowed = (option.dataset.gateValues ?? '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      const current = this.#answers.get(param)?.value ?? '';
      const show = current ? allowed.includes(current) : false;
      option.toggleAttribute('hidden', !show);
      if (!show && option.getAttribute('aria-checked') === 'true') {
        option.setAttribute('aria-checked', 'false');
        const step = option.closest('[data-atelier-step]');
        if (step?.dataset.param) this.#answers.delete(step.dataset.param);
      }
    }

    for (const group of this.querySelectorAll('[data-atelier-options]')) {
      const visible = [...group.querySelectorAll('[data-atelier-option]')].filter((o) => !o.hidden);
      if (!visible.length) continue;
      const checked = visible.find((o) => o.getAttribute('aria-checked') === 'true');
      for (const o of group.querySelectorAll('[data-atelier-option]')) o.tabIndex = -1;
      (checked ?? visible[0]).tabIndex = 0;
    }
  }

  #paintFlow() {
    const visible = this.#visibleSteps();
    const showingResults = this.#stepCursor >= visible.length && this.#answers.has('piece');

    for (const step of this.#steps) {
      const index = visible.indexOf(step);
      step.hidden = showingResults || index !== this.#stepCursor;
    }

    const progress = this.querySelector('[data-atelier-progress]');
    if (progress) {
      const template = this.#answers.has('piece')
        ? this.dataset.progressLabel || '[current] of [total]'
        : this.dataset.progressInitialLabel || 'Step [current]';
      progress.hidden = showingResults || !visible.length;
      progress.textContent = template
        .replace('[current]', String(Math.min(this.#stepCursor + 1, visible.length)))
        .replace('[total]', String(visible.length));
    }

    const nav = this.querySelector('[data-atelier-nav]');
    const skip = this.querySelector('[data-atelier-nav-skip]');
    const current = visible[this.#stepCursor];
    if (nav) nav.hidden = !visible.length;
    const back = this.querySelector('[data-atelier-back]');
    if (back) back.hidden = this.#stepCursor === 0 && !showingResults;
    if (skip) {
      skip.hidden = showingResults || current?.dataset.optional !== 'true';
    }

    if (!showingResults) {
      this.#resultsTarget && (this.#resultsTarget.innerHTML = '');
      this.querySelector('[data-atelier-bar]')?.setAttribute('hidden', '');
      this.querySelector('[data-atelier-more]')?.setAttribute('hidden', '');
      this.querySelector('[data-atelier-relax]')?.setAttribute('hidden', '');
      this.querySelector('[data-atelier-handoff]')?.setAttribute('hidden', '');
    }
  }

  #goBack() {
    const historyState = window.history.state;
    if (historyState?.amadaAtelier && Number(historyState.depth) > 0) {
      window.history.back();
      return;
    }

    const visible = this.#visibleSteps();
    if (!visible.length) return;

    if (this.#stepCursor >= visible.length) {
      this.#stepCursor = visible.length - 1;
    } else {
      this.#stepCursor = Math.max(0, this.#stepCursor - 1);
    }

    this.#clearAnswersFrom(this.#stepCursor);
    this.#applyShowFor();
    this.#applyGates();
    this.#syncUrl('replace');
    this.#refreshCounts();
    this.#paintChips();
    this.#paintFlow();
  }

  #clearAnswersFrom(startIndex) {
    const visible = this.#visibleSteps();
    for (let i = startIndex; i < visible.length; i++) {
      const step = visible[i];
      const param = step?.dataset.param;
      if (!param) continue;
      this.#answers.delete(param);
      step.querySelectorAll('[data-atelier-option]').forEach((option) => {
        option.setAttribute('aria-checked', 'false');
        option.removeAttribute('data-preview');
      });
      if (step.dataset.layout === 'shapes') {
        this.#previewOption = null;
        step.querySelectorAll('[data-atelier-shape-preview]').forEach((el) => {
          el.hidden = true;
        });
      }
    }

    const landed = visible[startIndex];
    if (landed?.dataset.param === 'piece') {
      this.#pool = [];
      this.#poolFor = null;
      this.#bandsForPiece = null;
      this.#poolFailed = false;
    }
  }

  #skipCurrent() {
    const visible = this.#visibleSteps();
    const step = visible[this.#stepCursor];
    if (!step) return;
    const skip = step.querySelector('[data-atelier-skip]');
    if (skip) {
      this.#select(step, skip, [...step.querySelectorAll('[data-atelier-option]')].filter((o) => !o.hidden));
      return;
    }
    if (step.dataset.param) this.#answers.delete(step.dataset.param);
    this.#syncUrl('push');
    this.#afterSelect(step);
  }

  #reset() {
    this.#selectionVersion++;
    this.#answers.clear();
    this.#bandsForPiece = null;
    this.#pool = [];
    this.#poolFor = null;
    this.#poolFailed = false;
    this.#previewOption = null;
    this.#stepCursor = 0;
    this.#ranked = [];
    this.#shown = 0;
    for (const option of this.querySelectorAll('[data-atelier-option]')) {
      option.setAttribute('aria-checked', 'false');
      option.removeAttribute('data-preview');
    }
    this.querySelectorAll('[data-atelier-shape-preview]').forEach((el) => {
      el.hidden = true;
    });
    if (this.#autoStep) {
      this.#autoStep.querySelectorAll('[data-atelier-option]').forEach((o) => o.remove());
    }
    this.#applyGates();
    this.#applyShowFor();
    this.#syncUrl('push');
    this.#paintChips();
    this.#paintFlow();
  }

  #syncUrl(mode = 'replace') {
    const url = new URL(window.location.href);
    for (const step of this.#steps) {
      const param = step.dataset.param;
      if (!param) continue;
      const answer = this.#answers.get(param);
      if (answer?.value) url.searchParams.set(param, answer.value);
      else url.searchParams.delete(param);
    }
    const currentState = window.history.state ?? {};
    const currentDepth = currentState.amadaAtelier ? Number(currentState.depth) || 0 : 0;
    const depth = mode === 'push' ? currentDepth + 1 : currentDepth;
    const state = { ...currentState, amadaAtelier: true, depth };
    if (mode === 'push') window.history.pushState(state, '', url);
    else window.history.replaceState(state, '', url);
  }

  async #restoreFromUrl() {
    const url = new URL(window.location.href);
    const restoreVersion = ++this.#selectionVersion;
    this.#answers.clear();
    this.#previewOption = null;
    this.#ranked = [];
    this.#shown = 0;

    for (const option of this.querySelectorAll('[data-atelier-option]')) {
      option.setAttribute('aria-checked', 'false');
      option.removeAttribute('data-preview');
      option.tabIndex = -1;
    }
    this.querySelectorAll('[data-atelier-shape-preview]').forEach((preview) => {
      preview.hidden = true;
    });

    for (const step of this.#steps) {
      if (step === this.#autoStep) continue;
      const param = step.dataset.param;
      const value = param && url.searchParams.get(param);
      if (!value) continue;
      const options = [...(step.querySelectorAll('[data-atelier-option]') ?? [])];
      const match =
        value === SKIP_VALUE
          ? options.find((option) => option.hasAttribute('data-atelier-skip'))
          : options.find((option) => option.dataset.value === value);
      if (!match) continue;
      match.setAttribute('aria-checked', 'true');
      match.tabIndex = 0;
      this.#answers.set(param, this.#answerFrom(match));
      if (step.dataset.layout === 'shapes' && !match.hasAttribute('data-atelier-skip')) {
        this.#previewShape(step, match);
      }
    }

    this.#applyGates();
    this.#applyShowFor();

    if (!this.#answers.has('piece')) {
      this.#pool = [];
      this.#poolFor = null;
      this.#bandsForPiece = null;
      this.#poolFailed = false;
      this.#stepCursor = 0;
      this.#paintChips();
      this.#paintFlow();
      return;
    }

    await this.#ensurePool();
    if (restoreVersion !== this.#selectionVersion) return;
    await this.#buildAutoBands();
    if (restoreVersion !== this.#selectionVersion) return;

    const budgetValue = url.searchParams.get('budget');
    if (budgetValue && this.#autoStep) {
      const chip =
        budgetValue === SKIP_VALUE
          ? this.#autoStep.querySelector('[data-atelier-skip]')
          : [...this.#autoStep.querySelectorAll('[data-atelier-option]')].find(
              (option) => option.dataset.value === budgetValue
            );
      if (chip) {
        chip.setAttribute('aria-checked', 'true');
        chip.tabIndex = 0;
        this.#answers.set('budget', this.#answerFrom(chip));
      }
    }

    const visible = this.#visibleSteps();
    const unanswered = visible.findIndex((step) => !this.#answers.has(step.dataset.param));
    if (unanswered === -1) {
      this.#shown = this.#shortlist;
      this.#renderResults();
      this.#stepCursor = visible.length;
    } else {
      this.#stepCursor = unanswered;
      this.#paintChips();
    }
    this.#paintFlow();
  }

  /**
   * One in-flight request per piece, shared by everyone who asks for it.
   *
   * The step now advances before the pool resolves, so a shopper can reach the
   * shape question while their piece is still loading. Without this, that second
   * answer aborted the first request and started the same fetch again.
   */
  #loadPool(key, piece) {
    const cached = this.#poolCache.get(key);
    if (cached) return Promise.resolve(cached);

    const inflight = this.#poolInflight.get(key);
    if (inflight) return inflight;

    const controller = new AbortController();
    const { signal } = controller;
    const request = (
      piece.collection
        ? this.#fetchCollection(piece.collection, signal)
        : this.#fetchSearch(piece.query || '*', signal)
    )
      .then((results) => {
        // An empty pool is a failure, not an answer worth remembering.
        if (results.length) this.#poolCache.set(key, results);
        return results;
      })
      .finally(() => {
        if (this.#poolInflight.get(key) === request) this.#poolInflight.delete(key);
        this.#poolControllers.delete(key);
      });

    this.#poolInflight.set(key, request);
    this.#poolControllers.set(key, controller);
    return request;
  }

  /** Warm a piece's pool on hover or focus, before it is chosen. */
  #prefetchPool(option) {
    if (!option || option.hasAttribute('data-atelier-skip')) return;
    const piece = this.#answerFrom(option);
    const key = piece.collection || piece.query;
    if (!key) return;
    if (this.#poolCache.has(key) || this.#poolInflight.has(key)) return;
    this.#loadPool(key, piece).catch(() => {});
  }

  async #ensurePool() {
    const piece = this.#answers.get('piece');
    if (!piece) {
      this.#pool = [];
      this.#poolFor = null;
      this.#poolFailed = false;
      return;
    }

    const key = piece.collection || piece.query || '*';
    if (this.#poolFor === key && this.#pool.length) return;

    this.#poolFailed = false;

    try {
      this.setAttribute('aria-busy', 'true');
      const results = await this.#loadPool(key, piece);
      if (this.#answers.get('piece')?.value !== piece.value) return;
      this.#pool = results;
      this.#poolFor = key;
      this.#poolFailed = false;
      const prices = results.map((r) => r.p).filter((p) => p > 0).sort((a, b) => a - b);
      this.#priceCache.set(piece.value || key, prices);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#pool = [];
      this.#poolFor = key;
      this.#poolFailed = true;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Fetch one page of an `?view=atelier` JSON template.
   *
   * No `Accept` header. Asking for `application/json` makes Shopify serve its
   * own built-in collection JSON — `{"collection": {…}}`, no `results` key —
   * and ignore the alternate template entirely. That silently returned an empty
   * pool on every request, so the finder always fell through to the slow
   * `products.json` path.
   */
  async #fetchJsonPage(url, signal) {
    const response = await fetch(url, { signal, credentials: 'same-origin' });
    if (!response.ok) return null;
    try {
      return JSON.parse(await response.text());
    } catch {
      return null;
    }
  }

  /**
   * Page 1 tells us how many pages there are; the rest are fetched together.
   * Sequential paging cost ~8s on a 353-product collection because each page is
   * a full Liquid render.
   */
  async #fetchJsonPages(makeUrl, signal) {
    const first = await this.#fetchJsonPage(makeUrl(1), signal);
    if (!first) return [];

    const out = [...(first.results ?? [])];
    const pages = Math.min(Number(first.pages) || 1, MAX_POOL_PAGES);
    if (pages < 2) return out;

    const rest = await Promise.all(
      Array.from({ length: pages - 1 }, (_, i) => this.#fetchJsonPage(makeUrl(i + 2), signal))
    );

    // Keep page order: results arrive price-ascending and the budget bands
    // depend on that ordering holding across the whole pool.
    for (const page of rest) {
      if (!page) break;
      out.push(...(page.results ?? []));
    }
    return out;
  }

  async #fetchCollection(handle, signal) {
    const inline = this.#inlinePool(handle);

    try {
      const viewResults = await this.#fetchJsonPages((page) => {
        const url = new URL(`/collections/${handle}`, window.location.origin);
        url.searchParams.set('view', 'atelier');
        url.searchParams.set('sort_by', 'price-ascending');
        if (page > 1) url.searchParams.set('page', String(page));
        return url;
      }, signal);
      if (viewResults.length) return viewResults;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
    }

    try {
      const ajaxResults = await this.#fetchAjaxCollection(handle, signal);
      if (ajaxResults.length) return ajaxResults;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
    }

    if (inline.length) return inline;
    throw new Error('collection-unavailable');
  }

  #inlinePool(handle) {
    if (!handle) return [];
    let node = null;
    try {
      node = this.querySelector(`script[data-atelier-pool="${CSS.escape(handle)}"]`);
    } catch {
      node = [...this.querySelectorAll('script[data-atelier-pool]')].find(
        (el) => el.getAttribute('data-atelier-pool') === handle
      );
    }
    const raw = node?.textContent?.trim();
    if (!raw) return [];
    try {
      const data = JSON.parse(raw);
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }

  async #fetchAjaxCollection(handle, signal) {
    const out = [];
    let reachedStore = false;
    for (let page = 1; page <= MAX_POOL_PAGES; page++) {
      const url = new URL(`/collections/${handle}/products.json`, window.location.origin);
      url.searchParams.set('limit', '50');
      url.searchParams.set('page', String(page));
      const response = await fetch(url, {
        signal,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        if (!reachedStore) throw new Error('collection-unavailable');
        break;
      }
      reachedStore = true;
      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error('collection-unavailable');
      }
      const products = data.products ?? [];
      out.push(...products.map((item) => this.#fromAjaxProduct(item)));
      if (products.length < 50) break;
    }
    return out;
  }

  #fromAjaxProduct(item) {
    const tags = Array.isArray(item.tags)
      ? item.tags
      : String(item.tags || '')
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);
    const image = item.featured_image || item.images?.[0];
    const img = typeof image === 'string' ? image : image?.src || null;
    const alt = (typeof image === 'object' && image?.alt) || item.title;
    const variants = Array.isArray(item.variants) ? item.variants : [];
    const prices = variants.map((variant) => this.#decimalPriceToPence(variant.price)).filter((price) => price > 0);
    const comparePrices = variants
      .map((variant) => this.#decimalPriceToPence(variant.compare_at_price))
      .filter((price) => price > 0);
    const fallbackPrice = this.#decimalPriceToPence(item.price_min ?? item.price);
    const p = prices.length ? Math.min(...prices) : fallbackPrice;
    const pmax = prices.length ? Math.max(...prices) : this.#decimalPriceToPence(item.price_max ?? item.price) || p;
    const c = comparePrices.length
      ? Math.min(...comparePrices)
      : this.#decimalPriceToPence(item.compare_at_price_min ?? item.compare_at_price);
    return {
      id: item.id,
      h: item.handle,
      t: item.title,
      u: `/products/${item.handle}`,
      p,
      pmax,
      c,
      a: variants.length ? variants.some((variant) => variant.available !== false) : item.available !== false,
      ty: item.product_type || item.type || '',
      shape: '',
      metal: '',
      img,
      alt,
      tags,
    };
  }

  #decimalPriceToPence(value) {
    const amount = Number(value);
    return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0;
  }

  #fetchSearch(expression, signal) {
    return this.#fetchJsonPages((page) => {
      const url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', expression);
      url.searchParams.set('view', 'atelier');
      url.searchParams.set('sort_by', 'price-ascending');
      if (page > 1) url.searchParams.set('page', String(page));
      return url;
    }, signal);
  }

  #termsFor(answer) {
    if (!answer || answer.skip) return [];
    if (answer.match) {
      return answer.match
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
    }
    const key = answer.shapeKey || answer.metalFamily || answer.value;
    return DEFAULT_MATCH[key] ?? (answer.label ? [answer.label.toLowerCase()] : []);
  }

  #haystack(product) {
    return [product.t, product.ty, product.shape, product.metal, ...(product.tags ?? [])]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  #includesTerm(hay, term) {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?:^|[^a-z0-9])${escaped}(?:[^a-z0-9]|$)`).test(hay);
  }

  #matchesAnswer(product, answer) {
    const terms = this.#termsFor(answer);
    if (!terms.length) return true;
    const hay = this.#haystack(product);
    return terms.some((term) => this.#includesTerm(hay, term));
  }

  #filteredPool() {
    return this.#pool.filter((product) => {
      for (const step of this.#steps) {
        const param = step.dataset.param;
        if (!param || param === 'budget' || param === 'piece') continue;
        const answer = this.#answers.get(param);
        if (!answer?.value || answer.skip) continue;
        if (!this.#matchesAnswer(product, answer)) return false;
      }
      return true;
    });
  }

  async #buildAutoBands() {
    const step = this.#autoStep;
    const piece = this.#answers.get('piece');
    if (!step || !piece) return;

    const pieceKey = piece.value || piece.collection || 'piece';
    if (this.#bandsForPiece === pieceKey && step.querySelector('[data-atelier-option]:not([data-atelier-skip])')) {
      this.#refreshCounts();
      step.hidden = false;
      return;
    }

    const group = step.querySelector('[data-atelier-options]');
    if (!group) return;

    const previous = this.#answers.get(step.dataset.param)?.value ?? '';
    const prices = this.#priceCache.get(pieceKey) ?? this.#pool.map((r) => r.p).filter((p) => p > 0).sort((a, b) => a - b);

    const edges = this.#edgesFromPrices(prices);
    group.querySelectorAll('[data-atelier-option]').forEach((o) => o.remove());

    for (let i = 0; i < edges.length - 1; i++) {
      const min = edges[i];
      const max = edges[i + 1];
      const label =
        min === 0
          ? `Under ${this.#money(max)}`
          : max === Infinity
            ? `${this.#money(min)} and up`
            : `${this.#money(min)} – ${this.#money(max)}`;

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'amada-atelier-option';
      chip.dataset.atelierOption = '';
      chip.dataset.value = `band-${i + 1}`;
      chip.dataset.label = label;
      if (min > 0) chip.dataset.priceMin = String(min / 100);
      if (max !== Infinity) chip.dataset.priceMax = String(max / 100);
      chip.setAttribute('role', 'radio');
      chip.setAttribute('aria-checked', 'false');
      chip.tabIndex = -1;
      chip.innerHTML =
        '<span class="amada-atelier-option__label"></span><span class="amada-atelier-option__count" data-atelier-option-count hidden></span>';
      chip.querySelector('.amada-atelier-option__label').textContent = label;
      group.append(chip);
    }

    if (step.dataset.optional === 'true') this.#appendSkipOption(group);

    const chips = [...group.querySelectorAll('[data-atelier-option]')];
    const restore =
      previous === SKIP_VALUE
        ? chips.find((chip) => chip.hasAttribute('data-atelier-skip'))
        : previous
          ? chips.find((chip) => chip.dataset.value === previous)
          : null;
    (restore ?? chips[0])?.setAttribute('tabIndex', '0');
    if (restore) {
      restore.setAttribute('aria-checked', 'true');
      this.#answers.set(step.dataset.param, this.#answerFrom(restore));
    } else if (previous) {
      this.#answers.delete(step.dataset.param);
    }

    this.#bandsForPiece = pieceKey;
    step.hidden = false;
    this.#refreshCounts();
  }

  #edgesFromPrices(prices) {
    if (!prices.length) {
      return [0, 80000, 125000, 250000, Infinity];
    }
    if (prices.length < 4) {
      const low = this.#roundBand(prices[0]);
      const high = this.#roundBand(prices[prices.length - 1]);
      return [...new Set([0, low, high, Infinity])];
    }
    const at = (f) => prices[Math.min(prices.length - 1, Math.floor(prices.length * f))];
    const cuts = [...new Set([at(0.25), at(0.5), at(0.75)].map((p) => this.#roundBand(p)))];
    return [0, ...cuts, Infinity];
  }

  #roundBand(pence) {
    const pounds = pence / 100;
    const step = pounds < 1000 ? 50 : pounds < 5000 ? 100 : 500;
    return Math.max(step, Math.round(pounds / step) * step) * 100;
  }

  #band() {
    let min = 0;
    let max = Infinity;
    for (const answer of this.#answers.values()) {
      if (answer.min) min = Math.max(min, answer.min * 100);
      if (answer.max) max = Math.min(max, answer.max * 100);
    }
    return { min, max };
  }

  #inPriceBand(price, min, max) {
    return price >= min && (max === Infinity || price < max);
  }

  #refreshCounts() {
    const filtered = this.#filteredPool();
    const shapeStep = this.#steps.find((s) => s.dataset.param === 'shape');
    const metalStep = this.#steps.find((s) => s.dataset.param === 'metal');
    const budgetStep = this.#autoStep;

    if (shapeStep) {
      for (const option of shapeStep.querySelectorAll('[data-atelier-option]')) {
        if (option.hasAttribute('data-atelier-skip')) continue;
        const answer = this.#answerFrom(option);
        const count = this.#pool.filter((p) => this.#matchesAnswer(p, answer)).length;
        this.#setCount(option, count);
        option.hidden = count === 0;
        option.removeAttribute('data-unavailable');
      }
    }

    if (metalStep) {
      for (const option of metalStep.querySelectorAll('[data-atelier-option]')) {
        if (option.hasAttribute('data-atelier-skip')) continue;
        const answer = this.#answerFrom(option);
        const count = filtered.filter((p) => this.#matchesAnswer(p, answer)).length;
        this.#setCount(option, count);
      }
    }

    if (budgetStep) {
      for (const option of budgetStep.querySelectorAll('[data-atelier-option]')) {
        if (option.hasAttribute('data-atelier-skip')) continue;
        const min = (Number(option.dataset.priceMin) || 0) * 100;
        const max = option.dataset.priceMax ? Number(option.dataset.priceMax) * 100 : Infinity;
        const count = filtered.filter((p) => this.#inPriceBand(p.p, min, max)).length;
        this.#setCount(option, count);
      }
    }
  }

  #setCount(option, total) {
    const node = option.querySelector('[data-atelier-option-count]');
    if (node) {
      node.textContent = total === 1 ? '1 piece' : `${total} pieces`;
      node.hidden = false;
    }
    option.setAttribute('data-count', String(total));
  }

  #renderResults() {
    const target = this.#resultsTarget;
    if (!target) return;

    const filtered = this.#filteredPool();
    const { min, max } = this.#band();
    const banded = min > 0 || max < Infinity;
    const inBand = filtered.filter((p) => this.#inPriceBand(p.p, min, max));
    this.#inBandCount = inBand.length;

    let ranked = [...inBand];
    let widened = false;

    if (banded && inBand.length < this.#minResults) {
      widened = true;
      ranked = [...filtered]
        .sort((a, b) => this.#distance(a.p, min, max) - this.#distance(b.p, min, max))
        .slice(0, Math.max(this.#minResults, inBand.length, this.#shortlist));
    } else {
      ranked.sort((a, b) => {
        const avail = Number(b.a) - Number(a.a);
        if (avail) return avail;
        return a.p - b.p;
      });
    }

    this.#ranked = ranked;
    this.#widened = widened;
    if (!this.#shown) this.#shown = this.#shortlist;

    const visible = ranked.slice(0, this.#shown);
    const root = document.createElement('div');
    root.className = 'amada-atelier-results';
    root.dataset.count = String(banded ? this.#inBandCount : filtered.length);

    const heading = document.createElement('h2');
    heading.className = 'amada-atelier-finder__count';
    heading.dataset.liveHeading = '';
    root.append(heading);

    const list = document.createElement('ul');
    list.className = 'amada-atelier-results__grid';
    list.setAttribute('role', 'list');

    const template = this.#cardTemplate;
    for (const product of visible) {
      if (!template) break;
      list.append(this.#cardFor(template, product, min, max));
    }

    root.append(list);
    target.replaceChildren(root);
    this.#placeBespoke(target, visible.length);
    this.#report(visible.length, widened, filtered.length);
    this.#paintChips();
    this.#updateWhatsAppLinks();
    window.AmadaWishlist?.hydrate(target);
    this.#focusResults(target);
  }

  #cardFor(template, product, min, max) {
    const node = template.content.firstElementChild.cloneNode(true);
    node.dataset.price = String(product.p);
    const link = node.querySelector('[data-card-link]');
    const view = node.querySelector('[data-card-view]');
    const img = node.querySelector('[data-card-image]');
    const title = node.querySelector('[data-card-title]');
    const spec = node.querySelector('[data-card-spec]');
    const why = node.querySelector('[data-card-why]');
    const price = node.querySelector('[data-card-price]');
    const compare = node.querySelector('[data-card-compare]');
    const save = node.querySelector('[data-card-save]');

    if (link) link.href = product.u;
    if (view) view.href = product.u;
    if (title) title.textContent = product.t;
    if (img) {
      if (product.img) {
        img.src = product.img;
        img.alt = product.alt || product.t;
      } else {
        img.remove();
      }
    }
    if (price) price.textContent = this.#money(product.p);
    if (compare) {
      if (product.c > product.p) {
        compare.textContent = this.#money(product.c);
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    }

    const shapeAnswer = this.#answers.get('shape');
    const metalAnswer = this.#answers.get('metal');
    const shape = (!shapeAnswer?.skip && shapeAnswer?.label) || this.#inferred(product, 'shape');
    const metal = (!metalAnswer?.skip && metalAnswer?.label) || this.#inferred(product, 'metal');
    const ready = this.#isReady(product);
    if (spec) {
      const parts = [shape, metal, ready ? 'Ready to ship' : ''].filter(Boolean);
      spec.textContent = parts.join(' · ');
      spec.hidden = !parts.length;
    }
    if (why) {
      const inBand = this.#inPriceBand(product.p, min, max);
      const raw = this.dataset.whyTemplate || '[shape] [metal], [price][ready]';
      why.textContent = raw
        .replace('[shape]', shape || '')
        .replace('[metal]', metal || '')
        .replace('[price]', this.#money(product.p))
        .replace('[ready]', ready ? ' — ready to ship' : inBand ? ' — in your budget' : '')
        .replace(/\s+/g, ' ')
        .trim();
      why.hidden = !why.textContent;
    }
    if (save) {
      save.dataset.productId = String(product.id ?? '');
      save.dataset.productHandle = product.h ?? '';
      save.setAttribute('aria-label', `${this.dataset.saveLabel || 'Save'} ${product.t}`);
    }
    return node;
  }

  #inferred(product, kind) {
    const hay = this.#haystack(product);
    if (kind === 'shape') {
      for (const [key, terms] of Object.entries(DEFAULT_MATCH)) {
        if (['platinum', 'white-gold', 'yellow-gold', 'rose-gold'].includes(key)) continue;
        if (terms.some((term) => this.#includesTerm(hay, term))) {
          return key.replace(/_/g, ' ');
        }
      }
    }
    if (kind === 'metal') {
      for (const key of ['platinum', 'rose-gold', 'yellow-gold', 'white-gold']) {
        if ((DEFAULT_MATCH[key] ?? []).some((term) => this.#includesTerm(hay, term))) {
          return key.replace(/-/g, ' ');
        }
      }
    }
    return '';
  }

  #isReady(product) {
    const needle = (this.dataset.readyTag || '#ready to ship').toLowerCase();
    return (product.tags ?? []).some((tag) => String(tag).toLowerCase().includes(needle.replace('#', '').trim()));
  }

  #placeBespoke(target, visibleCount) {
    const template = this.#bespokeTemplate;
    const grid = target.querySelector('.amada-atelier-results__grid');
    if (!template || !grid) return;

    target.querySelector('.amada-atelier-bespoke')?.closest('li')?.remove();
    const card = template.content.firstElementChild.cloneNode(true);
    const body = card.querySelector('[data-bespoke-body]');
    if (body?.textContent.includes('[answers]')) {
      const words = [...this.#answers.values()]
        .filter((a) => a.label && !a.skip && !a.min && !a.max)
        .map((a) => a.label.toLowerCase())
        .reverse()
        .join(' ');
      body.textContent = body.textContent.replace('[answers]', words || 'the piece you have in mind');
    }
    const promoteBelow = Number(this.dataset.bespokePromoteBelow) || 0;
    if (visibleCount < promoteBelow) grid.prepend(card);
    else grid.append(card);
  }

  #distance(price, min, max) {
    if (price < min) return min - price;
    if (max !== Infinity && price >= max) return price - max;
    return 0;
  }

  #report(rendered, widened, filteredTotal) {
    const band = this.#band();
    const banded = band.min > 0 || band.max < Infinity;
    const count = widened ? rendered : banded ? this.#inBandCount : Math.max(filteredTotal, rendered);
    const summary = this.#summaryText();
    const headingTpl = this.dataset.resultsHeading || '[count] [summary]';
    let message;

    if (this.#poolFailed) {
      message = this.dataset.loadError || 'Could not load this collection. Try again, or pick a different piece.';
    } else if (count === 0) message = this.dataset.countNone ?? '';
    else if (count === 1) message = this.dataset.countOne ?? '';
    else message = (this.dataset.countMany ?? '').replace('[count]', String(count));

    if (widened && rendered) {
      const nearest = Math.min(...this.#ranked.map((p) => p.p));
      const template = this.#inBandCount > 0 ? this.dataset.thinNote ?? '' : this.dataset.widenedNote ?? '';
      const note = template.replace('[price]', this.#money(nearest)).replace('[count]', String(this.#inBandCount));
      if (note) message = note;
    }

    const live = this.querySelector('[data-live-heading]') || this.querySelector('[data-atelier-count]');
    if (live) {
      live.textContent = headingTpl.replace('[count]', message || String(count)).replace('[summary]', summary);
    }

    this.querySelector('[data-atelier-bar]')?.removeAttribute('hidden');
    this.querySelector('[data-atelier-more]')?.toggleAttribute('hidden', this.#shown >= this.#ranked.length);
    this.querySelector('[data-atelier-handoff]')?.toggleAttribute('hidden', count > 0);

    const relax = this.querySelector('[data-atelier-relax]');
    const relaxBtn = this.querySelector('[data-atelier-relax-btn]');
    const drop = this.#relaxTarget();
    if (relax && relaxBtn) {
      if (count === 0 && drop && !this.#poolFailed) {
        relax.hidden = false;
        relaxBtn.textContent = (this.dataset.relaxLabel || 'Show without [answer]').replace(
          '[answer]',
          drop.label.toLowerCase()
        );
      } else {
        relax.hidden = true;
      }
    }
  }

  #summaryText() {
    const parts = [];
    for (const step of this.#steps) {
      const answer = this.#answers.get(step.dataset.param);
      if (answer?.label && !answer.skip) parts.push(answer.label.toLowerCase());
    }
    return parts.join(' ');
  }

  #updateWhatsAppLinks() {
    const answers = this.#summaryText() || 'a piece';
    const message = (this.dataset.whatsappMessage || "Hi Amada, I'm looking for [answers].")
      .replace('[answers]', answers);

    for (const link of this.querySelectorAll('[data-atelier-whatsapp]')) {
      const baseHref = link.dataset.baseHref || link.getAttribute('href');
      if (!baseHref) continue;
      link.dataset.baseHref = baseHref;
      try {
        const url = new URL(baseHref, window.location.origin);
        url.searchParams.set('text', message);
        link.href = url.toString();
      } catch {
        /* Keep the merchant-provided URL if it cannot be parsed. */
      }
    }
  }

  #paintChips() {
    const node = this.querySelector('[data-atelier-chips]');
    if (!node) return;
    node.replaceChildren();
    for (const step of this.#steps) {
      const answer = this.#answers.get(step.dataset.param);
      if (!answer?.label || answer.skip) continue;
      const chip = document.createElement('span');
      chip.className = 'amada-atelier-finder__chip';
      chip.innerHTML = `<span></span><button type="button" class="amada-atelier-finder__chip-remove" data-remove-param="${step.dataset.param}" aria-label="Remove ${answer.label}">×</button>`;
      chip.querySelector('span').textContent = answer.label;
      node.append(chip);
    }
    node.hidden = !node.childElementCount;
  }

  #onChipRemove(event) {
    const btn = event.target.closest('[data-remove-param]');
    if (!btn) return;
    const param = btn.dataset.removeParam;
    this.#answers.delete(param);
    const step = this.#steps.find((s) => s.dataset.param === param);
    step?.querySelectorAll('[data-atelier-option]').forEach((o) => o.setAttribute('aria-checked', 'false'));
    if (param === 'piece') {
      this.#reset();
      return;
    }
    this.#syncUrl('push');
    this.#refreshCounts();
    this.#shown = this.#shortlist;
    this.#renderResults();
    this.#paintFlow();
  }

  #relaxTarget() {
    const order = ['metal', 'shape', 'budget'];
    for (const param of order) {
      const answer = this.#answers.get(param);
      if (answer?.label && !answer.skip) return { param, label: answer.label };
    }
    return null;
  }

  #relax() {
    const drop = this.#relaxTarget();
    if (!drop) return;
    this.#answers.delete(drop.param);
    const step = this.#steps.find((s) => s.dataset.param === drop.param);
    step?.querySelectorAll('[data-atelier-option]').forEach((o) => o.setAttribute('aria-checked', 'false'));
    this.#syncUrl('push');
    this.#refreshCounts();
    this.#shown = this.#shortlist;
    this.#renderResults();
    this.#paintFlow();
  }

  #loadMore() {
    this.#shown += this.#shortlist;
    this.#renderResults();
  }

  #money(pence) {
    const amount = pence / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'GBP',
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `£${Math.round(amount)}`;
    }
  }

  #focusResults(target) {
    target.focus({ preventScroll: true });
    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
    target.style.scrollMarginBlockStart = `${SCROLL_MARGIN}px`;
  }
}

if (!customElements.get('amada-atelier-finder')) {
  customElements.define('amada-atelier-finder', AmadaAtelierFinder);
}
